export type ProposalStatus = "received" | "reviewing" | "planned" | "closed";
export type MessageKind =
  "text" | "photo" | "video" | "voice" | "video_note" | "document" | "sticker";

export type UserState = "active" | "unreachable" | "blocked";

export interface PublicUser {
  id: number;
  firstName: string;
  lastName?: string;
  username?: string;
  photoUrl?: string;
  languageCode?: string;
  isPremium: boolean;
  broadcastEnabled: boolean;
  consentedAt?: string;
  firstSeenAt: string;
  lastSeenAt: string;
  state: UserState;
  archived: boolean;
  muted: boolean;
  unreadCount: number;
  tags?: Tag[];
}

export interface Tag {
  id: string;
  name: string;
  color: string;
}

export interface Attachment {
  id: string;
  name: string;
  mimeType: string;
  size: number;
  url?: string;
}

export interface Proposal {
  id: string;
  userId: number;
  title: string;
  description: string;
  links: string[];
  status: ProposalStatus;
  adminComment?: string;
  version: number;
  attachments: Attachment[];
  createdAt: string;
  updatedAt: string;
  isViewed?: boolean;
  user?: PublicUser;
}

export interface ProposalRevision {
  id: string;
  proposalId: string;
  version: number;
  title: string;
  description: string;
  links: string[];
  attachments: Attachment[];
  createdAt: string;
}

export interface ConversationMessage {
  id: string;
  userId: number;
  direction: "inbound" | "outbound";
  source: "telegram" | "miniapp" | "admin" | "broadcast" | "system";
  kind: MessageKind;
  text?: string;
  replyToMessageId?: string;
  replyPreview?: string;
  telegramMessageId?: number;
  fileId?: string;
  attachment?: Attachment;
  contextType?: "proposal";
  contextId?: string;
  createdAt: string;
}

export type BroadcastStatus =
  | "draft"
  | "scheduled"
  | "queued"
  | "sending"
  | "completed"
  | "cancelled"
  | "failed";

export interface BroadcastButton {
  label: string;
  url: string;
}

export interface BroadcastContent {
  kind: Exclude<MessageKind, "sticker">;
  text?: string;
  caption?: string;
  fileId?: string;
  attachmentId?: string;
  buttons?: BroadcastButton[];
}

export interface BroadcastFilter {
  tagIds?: string[];
  activeSince?: string;
  proposalStatuses?: ProposalStatus[];
  userIds?: number[];
  testOnlyAdmin?: boolean;
}

export interface Broadcast {
  id: string;
  title: string;
  status: BroadcastStatus;
  content: BroadcastContent;
  filter: BroadcastFilter;
  scheduledAt?: string;
  recipientTotal: number;
  pendingCount: number;
  sentCount: number;
  failedCount: number;
  optedOutCount: number;
  clickCount: number;
  createdAt: string;
}

export interface BroadcastDelivery {
  id: string;
  broadcastId: string;
  userId: number;
  status: "pending" | "retry" | "sent" | "failed" | "skipped";
  attempts: number;
  error?: string;
  sentAt?: string;
  createdAt: string;
  user: PublicUser;
}

export interface DashboardStats {
  users: number;
  activeUsers: number;
  unreadMessages: number;
  newProposals: number;
  scheduledBroadcasts: number;
  testMode: boolean;
}

export const PROPOSAL_STATUS_LABELS: Record<ProposalStatus, string> = {
  received: "Получена",
  reviewing: "На рассмотрении",
  planned: "Запланирована",
  closed: "Закрыта",
};

export const MAX_ATTACHMENT_SIZE = 20 * 1024 * 1024;
export const MAX_ATTACHMENTS = 5;
export const MAX_TOTAL_ATTACHMENT_SIZE = 50 * 1024 * 1024;

export const canEditProposal = (status: ProposalStatus): boolean =>
  status === "received";

export function saratovLocalToIso(value: string): string {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value))
    throw new Error("Некорректная дата");
  const date = new Date(`${value}:00+04:00`);
  if (Number.isNaN(date.getTime())) throw new Error("Некорректная дата");
  return date.toISOString();
}
